import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import Swal from "sweetalert2";

// Convert a string to camel case (e.g., "first name" => "firstName")
const toCamelCase = (str) => {
  return str
    .toLowerCase()
    .replace(/[-_ ]+(.)/g, (match, group1) => group1.toUpperCase());
};

const CustomFields = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [customFields, setCustomFields] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [field, setField] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [error, setError] = useState("");

  const [removeModal, setRemoveModal] = useState({
    isOpen: false,
    fieldToDelete: null,
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Toggle the add/edit modal
  const toggleModal = () => {
    setModalOpen(!modalOpen);
    setError("");
  };

  // Open the add modal
  const openAddModal = () => {
    setModalType("add");
    setField(""); // Clear field for adding new field
    setEditIndex(null); // Clear any selected field index
    toggleModal();
  };

  // Open the edit modal with the selected field's value
  const openEditModal = (field) => {
    setField({ ...field, originalName: field.name });
    setModalType("edit");
    toggleModal();
  };

  // Handle field name change
  const handleFieldChange = (e) => {
    setField({ ...field, name: e.target.value });
  };

  // Fetch custom fields from the server
  const fetchCustomFields = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/custom-fields`, {
        headers: getAuthHeaders(),
      });
      setCustomFields(response.data.data || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching custom fields:", error);
    }
  };

  // Handle adding a new custom field
  const handleAddField = async () => {
    try {
      const camelCaseField = toCamelCase(field.name);
      const response = await axios.post(
        `${API_BASE_URL}/custom-fields/store`,
        { name: camelCaseField },
        { headers: getAuthHeaders() }
      );
      if (response.data && response.data.success === false) {
        setError(response.data.message || "Failed to add field.");

        Swal.fire({
          title: "Error!",
          text: `${response.data.message || "Failed to add field."}`,
          icon: "error",
          confirmButtonText: "OK",
        });
      } else {
        fetchCustomFields();
        setError(null);
        toggleModal();

        Swal.fire({
          title: "Success!",
          text: "Field added successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      setError("An error occurred while adding the custom field.");
      console.error("Error adding custom field:", error);

      Swal.fire({
        title: "Error!",
        text: "An error occurred while adding the custom field.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Handle editing an existing custom field
  const handleEditField = async () => {
    try {
      const camelCaseField = toCamelCase(field.name);
      const response = await axios.put(
        `${API_BASE_URL}/custom-fields/update/${field.originalName}`,
        { name: camelCaseField },
        { headers: getAuthHeaders() }
      );
      if (response.data && response.data.success === false) {
        setError(response.data.message || "Failed to update field.");

        Swal.fire({
          title: "Error!",
          text: `${response.data.message || "Failed to update field."}`,
          icon: "error",
          confirmButtonText: "OK",
        });
      } else {
        fetchCustomFields();
        setError(null);
        toggleModal();

        Swal.fire({
          title: "Success!",
          text: "Field updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      setError("An error occurred while updating the custom field.");
      console.error("Error updating custom field:", error);

      Swal.fire({
        title: "Error!",
        text: "An error occurred while updating the custom field.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDeleteField = async (name) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/custom-fields/delete/${name}`, {
        headers: getAuthHeaders(),
      });

      if (response.data && response.data.success === false) {
        Swal.fire({
          title: "Error!",
          text: response.data.message || "Failed to delete field.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } else {
        fetchCustomFields();

        Swal.fire({
          title: "Success!",
          text: "Field deleted successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error deleting custom field:", error);

      Swal.fire({
        title: "Error!",
        text: "An error occurred while deleting the custom field.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const openRemoveModal = (fieldName) => {
    setRemoveModal({ isOpen: true, fieldToDelete: fieldName });
  };

  const closeRemoveModal = () => {
    setRemoveModal({ isOpen: false, fieldToDelete: null });
  };

  // Delete the selected field
  const confirmDeleteField = async () => {
    if (removeModal.fieldToDelete) {
      await handleDeleteField(removeModal.fieldToDelete);
      closeRemoveModal();
    }
  };
  useEffect(() => {
    fetchCustomFields();
  }, []);

  return (
    <Container fluid className="p-4">
      <Card className="shadow rounded-3 border-0">
        <CardHeader className="bg-white border-bottom d-flex justify-content-between align-items-center">
          <h3 style={{ fontWeight: "700" }} className="fw-bold mb-0">Custom Fields</h3>
          <Button style={{
            backgroundColor: "#004992",
            border: "none",
            fontWeight: "600",
            borderRadius: "8px",
            padding: "8px 25px",
          }} onClick={openAddModal} className="px-4">
            + Add Field
          </Button>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2">Loading custom fields...</p>
            </div>
          ) : (
            <Table responsive borderless className="align-middle">
              <thead>
                <tr className="bg-light text-uppercase small text-muted">
                  <th>#</th>
                  <th>Field Name</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customFields.length > 0 ? (
                  customFields.map((field, index) => (
                    <tr key={index} className="border-bottom">
                      <td>{index + 1}</td>
                      <td className="fw-semibold text-capitalize">{field.name}</td>
                      <td className="text-end">
                        <Button color="outline-dark" size="sm" className="me-2" onClick={() => openEditModal(field)}>
                          Edit
                        </Button>
                        <Button color="outline-danger" size="sm" onClick={() => setRemoveModal({ isOpen: true, fieldToDelete: field.name })}>
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-4 text-muted">No custom fields found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>
          <div style={{ fontWeight: 700, fontSize: "1.5rem" }}>
            {modalType === "add" ? "Add Custom Field" : "Edit Custom Field"}
          </div>
        </ModalHeader>

        <ModalBody>
          {error && <div className="alert alert-danger fw-semibold">{error}</div>}
          <Label for="fieldName" className="fw-semibold mb-2">
            Field Name <span className="text-danger">*</span>
          </Label>
          <Input
            id="fieldName"
            type="text"
            value={field.name || ""}
            onChange={handleFieldChange}
            placeholder="Enter field name"
            className="mb-2"
            style={{ border: "2px solid #DBDBDB", borderRadius:'8px' }}
          />
          <div  style={{ fontWeight: 700,}} className="text-muted small">
            <i className="fas fa-info-circle me-1"></i>
            Field names will automatically be converted to camelCase.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            className="fw-semibold"
            onClick={modalType === "add" ? handleAddField : handleEditField}
          >
            {modalType === "add" ? "Add Field" : "Update Field"}
          </Button>
          <Button color="secondary" className="fw-semibold" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>


      {/* Delete Confirmation Modal */}
      <Modal isOpen={removeModal.isOpen} toggle={() => setRemoveModal({ isOpen: false, fieldToDelete: null })} centered>
        <ModalHeader toggle={() => setRemoveModal({ isOpen: false, fieldToDelete: null })} className="fw-bold">
          Confirm Removal
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the custom field <strong>{removeModal.fieldToDelete}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" className="fw-semibold" onClick={confirmDeleteField}>
            Delete
          </Button>
          <Button color="secondary" className="fw-semibold" onClick={() => setRemoveModal({ isOpen: false, fieldToDelete: null })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default CustomFields;
